<template>
  <div class="nontax-compare-panel">
    <div class="modal-header">
      <h2>임금개선 분석 보고서</h2>
      <v-icon v-if="showClose" class="close-btn" @click="emit('close')">mdi-close</v-icon>
    </div>

    <div class="info-section">
      <div class="info-row">
        <div class="info-item">
          <span class="info-label">조회 연월</span>
          <span class="info-value highlight">{{ currentYearMonth }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">대상 인원</span>
          <span class="info-value">{{ comparisonData.length }}명</span>
        </div>
      </div>
    </div>

    <div v-if="!hideScenarioSettings" class="setting-section">
      <div class="setting-title">비과세 미적용 시나리오 설정</div>
      <div class="setting-row">
        <div class="setting-item lunch-setting">
          <label class="setting-label">중식비 금액 (전 직원 동일 적용)</label>
          <v-text-field
            :model-value="customLunch"
            type="text"
            density="compact"
            variant="outlined"
            hide-details
            class="lunch-input"
            placeholder="0"
            @update:model-value="onCustomLunchInput"
          />
          <span class="setting-hint">※ 현재 중식비가 더 작으면 현재 금액 유지</span>
        </div>
        <div class="setting-item">
          <v-checkbox
            :model-value="noProductionNontax"
            label="생산직 비과세 모두 미적용 (연장/야간/휴일 수당 비과세 제외)"
            hide-details
            density="compact"
            color="primary"
            @update:model-value="onNoProductionChange"
          />
        </div>
      </div>
    </div>

    <div class="modal-content" v-if="comparisonData.length > 0">
      <v-tabs :model-value="activeTab" color="primary" @update:model-value="emit('update:activeTab', $event)">
        <v-tab value="detail">상세 비교</v-tab>
        <v-tab value="summary">요약</v-tab>
      </v-tabs>

      <v-window :model-value="activeTab">
        <v-window-item value="detail">
          <div class="unit-label">단위: 원</div>
          <div class="comparison-table-wrapper">
            <table class="comparison-table">
              <thead>
                <tr>
                  <th rowspan="2" class="sticky-col col-no">No</th>
                  <th rowspan="2" class="sticky-col-name">사원명</th>
                  <th rowspan="2">직종</th>
                  <th rowspan="2">총급여</th>
                  <th colspan="4" class="before-header">비과세 미적용시</th>
                  <th colspan="4" class="after-header">비과세 적용시 (현재)</th>
                  <th colspan="4" class="diff-header">차액(비과세 미적용 - 적용(현재))</th>
                  <th rowspan="2" class="saving-header">절감액</th>
                  <th rowspan="2" class="risk-header">통상임금<br>위험액</th>
                </tr>
                <tr>
                  <th class="before-header">비과세</th>
                  <th class="before-header">기관부담</th>
                  <th class="before-header">퇴직금</th>
                  <th class="before-header">실수령</th>
                  <th class="after-header">비과세</th>
                  <th class="after-header">기관부담</th>
                  <th class="after-header">퇴직금</th>
                  <th class="after-header">실수령</th>
                  <th class="diff-sub-header">비과세</th>
                  <th class="diff-sub-header">기관부담</th>
                  <th class="diff-sub-header">퇴직금</th>
                  <th class="diff-sub-header">실수령</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in comparisonData" :key="index">
                  <td class="sticky-col col-no text-center">{{ index + 1 }}</td>
                  <td class="sticky-col-name">{{ item.empName }}</td>
                  <td class="text-center">{{ item.empJob }}</td>
                  <td class="text-right">{{ item.total }}</td>
                  <td class="before-cell text-right">{{ item.before.nonTax }}</td>
                  <td class="before-cell text-right">{{ item.before.companyIns }}</td>
                  <td class="before-cell text-right">{{ item.before.retirement }}</td>
                  <td class="before-cell text-right">{{ item.before.netPay }}</td>
                  <td class="after-cell text-right">{{ item.after.nonTax }}</td>
                  <td class="after-cell text-right">{{ item.after.companyIns }}</td>
                  <td class="after-cell text-right">{{ item.after.retirement }}</td>
                  <td class="after-cell text-right">{{ item.after.netPay }}</td>
                  <td class="diff-cell text-right" :class="getDiffClass(item.diff.nonTax)">
                    <nontax-diff-tooltip-cell :text="item.diff.nonTax" :tooltip="item.diffTooltip?.nonTax" />
                  </td>
                  <td class="diff-cell text-right" :class="getDiffClass(item.diff.companyIns, true)">
                    <nontax-diff-tooltip-cell :text="item.diff.companyIns" :tooltip="item.diffTooltip?.companyIns" />
                  </td>
                  <td class="diff-cell text-right" :class="getDiffClass(item.diff.retirement, true)">
                    <nontax-diff-tooltip-cell :text="item.diff.retirement" :tooltip="item.diffTooltip?.retirement" />
                  </td>
                  <td class="diff-cell text-right" :class="getDiffClass(item.diff.netPay)">
                    <nontax-diff-tooltip-cell :text="item.diff.netPay" :tooltip="item.diffTooltip?.netPay" />
                  </td>
                  <td class="saving-cell text-right font-bold" :class="getDiffClass(item.saving, true)">
                    <nontax-diff-tooltip-cell :text="item.saving" :tooltip="item.diffTooltip?.saving" />
                  </td>
                  <td class="risk-cell text-right font-bold">
                    <nontax-diff-tooltip-cell
                      :text="item.ordinaryWageRiskMonthly"
                      :tooltip="item.diffTooltip?.ordinaryWageRisk"
                    />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td class="sticky-col col-no font-bold" colspan="3">합계</td>
                  <td class="text-right font-bold">{{ totals.total }}</td>
                  <td class="before-cell text-right font-bold">{{ totals.before.nonTax }}</td>
                  <td class="before-cell text-right font-bold">{{ totals.before.companyIns }}</td>
                  <td class="before-cell text-right font-bold">{{ totals.before.retirement }}</td>
                  <td class="before-cell text-right font-bold">{{ totals.before.netPay }}</td>
                  <td class="after-cell text-right font-bold">{{ totals.after.nonTax }}</td>
                  <td class="after-cell text-right font-bold">{{ totals.after.companyIns }}</td>
                  <td class="after-cell text-right font-bold">{{ totals.after.retirement }}</td>
                  <td class="after-cell text-right font-bold">{{ totals.after.netPay }}</td>
                  <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.nonTax)">
                    <nontax-diff-tooltip-cell :text="totals.diff.nonTax" :tooltip="totals.diffTooltip?.nonTax" />
                  </td>
                  <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.companyIns, true)">
                    <nontax-diff-tooltip-cell :text="totals.diff.companyIns" :tooltip="totals.diffTooltip?.companyIns" />
                  </td>
                  <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.retirement, true)">
                    <nontax-diff-tooltip-cell :text="totals.diff.retirement" :tooltip="totals.diffTooltip?.retirement" />
                  </td>
                  <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.netPay)">
                    <nontax-diff-tooltip-cell :text="totals.diff.netPay" :tooltip="totals.diffTooltip?.netPay" />
                  </td>
                  <td class="saving-cell text-right font-bold" :class="getDiffClass(totals.saving, true)">
                    <nontax-diff-tooltip-cell :text="totals.saving" :tooltip="totals.diffTooltip?.saving" />
                  </td>
                  <td class="risk-cell text-right font-bold">
                    <nontax-diff-tooltip-cell
                      :text="totals.ordinaryWageRiskMonthly"
                      :tooltip="totals.diffTooltip?.ordinaryWageRisk"
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </v-window-item>

        <v-window-item value="summary">
          <div class="summary-section">
            <div class="summary-cards">
              <div class="summary-card before-card">
                <h3>비과세 미적용시</h3>
                <p class="card-desc">{{ beforeDescription }}</p>
                <div class="summary-row">
                  <span class="label">총급여</span>
                  <span class="value">{{ totals.total }}</span>
                </div>
                <div class="summary-row">
                  <span class="label">비과세</span>
                  <span class="value">{{ totals.before.nonTax }}</span>
                </div>
                <div class="divider"></div>
                <div class="summary-row">
                  <span class="label">기관부담 4대보험</span>
                  <span class="value">{{ totals.before.companyIns }}</span>
                </div>
                <div class="summary-row">
                  <span class="label">퇴직금</span>
                  <span class="value">{{ totals.before.retirement }}</span>
                </div>
                <div class="divider"></div>
                <div class="summary-row total">
                  <span class="label">실수령액</span>
                  <span class="value">{{ totals.before.netPay }}</span>
                </div>
              </div>

              <div class="summary-card after-card">
                <h3>비과세 적용시 (현재)</h3>
                <p class="card-desc">생산직비과세 적용 (연장/야간/휴일 포함)</p>
                <div class="summary-row">
                  <span class="label">총급여</span>
                  <span class="value">{{ totals.total }}</span>
                </div>
                <div class="summary-row">
                  <span class="label">비과세</span>
                  <span class="value">{{ totals.after.nonTax }}</span>
                </div>
                <div class="divider"></div>
                <div class="summary-row">
                  <span class="label">기관부담 4대보험</span>
                  <span class="value">{{ totals.after.companyIns }}</span>
                </div>
                <div class="summary-row">
                  <span class="label">퇴직금</span>
                  <span class="value">{{ totals.after.retirement }}</span>
                </div>
                <div class="divider"></div>
                <div class="summary-row total">
                  <span class="label">실수령액</span>
                  <span class="value">{{ totals.after.netPay }}</span>
                </div>
              </div>

              <div class="summary-card diff-card">
                <h3>기관 비용 절감 효과</h3>
                <div class="summary-row highlight-row">
                  <span class="label">대상 인원</span>
                  <span class="value">{{ comparisonData.length }}명</span>
                </div>
                <div class="divider"></div>
                <div class="summary-row">
                  <span class="label">기관부담 4대보험 차액</span>
                  <span class="value" :class="getDiffClass(totals.diff.companyIns, true)">{{ totals.diff.companyIns }}</span>
                </div>
                <div class="summary-row">
                  <span class="label">퇴직금 차액</span>
                  <span class="value" :class="getDiffClass(totals.diff.retirement, true)">{{ totals.diff.retirement }}</span>
                </div>
                <div class="divider"></div>
                <div class="summary-row total">
                  <span class="label">월 절감액</span>
                  <span class="value large" :class="getDiffClass(totals.saving, true)">{{ totals.saving }}</span>
                </div>
                <div class="summary-row info">
                  <span class="label">연간 절감 예상액</span>
                  <span class="value" :class="getDiffClass(totals.saving, true)">{{ totals.yearSaving }}</span>
                </div>
              </div>

              <div class="summary-card risk-card">
                <h3>통상임금 위험액</h3>
                <p class="card-desc">중식비·직책수당 통상임금 포함 시 연장·야간·퇴직·연차 소급 추정</p>
                <div class="summary-row highlight-row">
                  <span class="label">1년 추정치</span>
                  <span class="value large risk-value">{{ totals.ordinaryWageRisk1Year }}</span>
                </div>
                <div class="divider"></div>
                <div class="summary-row total">
                  <span class="label">3년 추정치</span>
                  <span class="value large risk-value">{{ totals.ordinaryWageRisk }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-window-item>
      </v-window>
    </div>

    <div class="modal-content empty-state" v-else>
      <p>데이터가 없습니다.</p>
    </div>

    <div class="modal-footer">
      <v-btn
        variant="flat"
        class="btn-search"
        :disabled="comparisonData.length === 0"
        :loading="isGeneratingPdf"
        @click="emit('generate-pdf')"
      >
        <v-icon left size="small">mdi-file-pdf-box</v-icon>
        PDF 출력
      </v-btn>
      <v-spacer />
      <v-btn v-if="showClose" variant="text" class="btn-cancel" @click="emit('close')">닫기</v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  NontaxComparisonItem,
  NontaxComparisonTotals,
} from '@/components/wage/common/CommonWageNontaxCompare.vue';
import NontaxDiffTooltipCell from '@/components/wage/common/NontaxDiffTooltipCell.vue';

const props = withDefaults(
  defineProps<{
    activeTab: string;
    currentYearMonth: string;
    comparisonData: NontaxComparisonItem[];
    totals: NontaxComparisonTotals;
    customLunch: string;
    noProductionNontax: boolean;
    beforeDescription: string;
    isGeneratingPdf: boolean;
    showClose?: boolean;
    hideScenarioSettings?: boolean;
  }>(),
  {
    showClose: true,
    hideScenarioSettings: false,
  },
);

const emit = defineEmits<{
  'update:activeTab': [value: string];
  'update:customLunch': [value: string];
  'update:noProductionNontax': [value: boolean];
  'setting-change': [];
  'generate-pdf': [];
  close: [];
}>();

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, '')) || 0;
};

const getDiffClass = (value: string, _isReverse = false): string => {
  const num = nc(value);
  if (num > 0) return 'highlight-green';
  if (num < 0) return 'highlight-red';
  return '';
};

const onCustomLunchInput = (value: string) => {
  emit('update:customLunch', value);
  emit('setting-change');
};

const onNoProductionChange = (value: boolean | null) => {
  emit('update:noProductionNontax', Boolean(value));
  emit('setting-change');
};
</script>
